/**
 * Projects → Boards → Columns — Zod schemas (Milestone 2.4, spec §3.6).
 *
 * Mirrors:
 *   - chaos.projects                (extended in M2.1 with org_id, instructions, display_name, etc.)
 *   - chaos.boards                  (NEW in M2.1)
 *   - chaos.columns                 (extended in M2.1 with board_id, instructions, slug, display_name)
 *
 * Wire convention: camelCase fields, ISO datetime strings, plain uuid
 * strings. Soft delete via `archivedAt` (nullable ISO).
 *
 * Naming note:
 *   The package already exports `projectSchema/Project` (project.ts,
 *   M1 legacy) and `boardSchema/Board/columnSchema/Column` (kanban.ts).
 *   To prevent re-export collisions through @chaos/types/index, this
 *   file uses the `OrgScoped*` prefix on the v2 entities. Consumers
 *   that want the new wire-shape import these names; consumers that
 *   need the legacy shape keep using the existing names.
 */
import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────
// Shared shape primitives
// ─────────────────────────────────────────────────────────────────────
const slugRegex = /^[a-z0-9][a-z0-9-]*$/;

const slugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(slugRegex, 'lowercase alphanumeric + dashes only');

const positionSchema = z.number().finite();

// ─────────────────────────────────────────────────────────────────────
// Project (org-scoped, M2 canonical)
// ─────────────────────────────────────────────────────────────────────
export const orgScopedProjectSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid().nullable(),
  slug: z.string(),
  displayName: z.string(),
  description: z.string().nullable(),
  instructions: z.string().nullable(),
  icon: z.string().nullable(),
  // Settings V2 (0064) — drag-to-reorder position within a company. NULL
  // when never reordered (gateway then falls back to created_at order).
  position: z.number().nullable().catch(null),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OrgScopedProject = z.infer<typeof orgScopedProjectSchema>;

// Settings V2 (0064) — reorder projects within a company (org-scoped,
// founder/admin gated). Mirrors reorderOrgScopedColumnsSchema.
export const reorderOrgScopedProjectsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().uuid(),
        position: positionSchema,
      }),
    )
    .min(1),
});
export type ReorderOrgScopedProjectsInput = z.infer<typeof reorderOrgScopedProjectsSchema>;

export const createOrgScopedProjectSchema = z.object({
  slug: slugSchema,
  displayName: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  instructions: z.string().max(200_000).nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
});
export type CreateOrgScopedProjectInput = z.infer<typeof createOrgScopedProjectSchema>;

export const updateOrgScopedProjectSchema = z.object({
  displayName: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  instructions: z.string().max(200_000).nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
});
export type UpdateOrgScopedProjectInput = z.infer<typeof updateOrgScopedProjectSchema>;

// ─────────────────────────────────────────────────────────────────────
// Board (M2 canonical — chaos.boards)
// ─────────────────────────────────────────────────────────────────────
export const orgScopedBoardViewTypes = ['kanban', 'list', 'gantt'] as const;
export const orgScopedBoardViewTypeSchema = z.enum(orgScopedBoardViewTypes);
export type OrgScopedBoardViewType = z.infer<typeof orgScopedBoardViewTypeSchema>;

export const orgScopedBoardSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  orgId: z.string().uuid(),
  slug: z.string(),
  displayName: z.string(),
  description: z.string().nullable(),
  viewType: orgScopedBoardViewTypeSchema,
  instructions: z.string().nullable(),
  icon: z.string().nullable(),
  // Settings V2 (0064) — drag-to-reorder position within a project. NULL
  // when never reordered (gateway then falls back to created_at order).
  position: z.number().nullable().catch(null),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OrgScopedBoard = z.infer<typeof orgScopedBoardSchema>;

// Settings V2 (0064) — reorder boards within a project (project-scoped,
// founder/admin gated). Mirrors reorderOrgScopedColumnsSchema.
export const reorderOrgScopedBoardsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().uuid(),
        position: positionSchema,
      }),
    )
    .min(1),
});
export type ReorderOrgScopedBoardsInput = z.infer<typeof reorderOrgScopedBoardsSchema>;

export const createOrgScopedBoardSchema = z.object({
  slug: slugSchema,
  displayName: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  viewType: orgScopedBoardViewTypeSchema.optional(),
  instructions: z.string().max(200_000).nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
});
export type CreateOrgScopedBoardInput = z.infer<typeof createOrgScopedBoardSchema>;

export const updateOrgScopedBoardSchema = z.object({
  displayName: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).nullable().optional(),
  viewType: orgScopedBoardViewTypeSchema.optional(),
  instructions: z.string().max(200_000).nullable().optional(),
  icon: z.string().max(120).nullable().optional(),
});
export type UpdateOrgScopedBoardInput = z.infer<typeof updateOrgScopedBoardSchema>;

// ─────────────────────────────────────────────────────────────────────
// Column (M2 canonical — extends legacy chaos.columns row with
// board_id, instructions, slug, display_name).
// ─────────────────────────────────────────────────────────────────────
export const orgScopedColumnSchema = z.object({
  id: z.string().uuid(),
  boardId: z.string().uuid().nullable(),
  projectId: z.string().uuid(),
  slug: z.string().nullable(),
  name: z.string(), // legacy field still present
  displayName: z.string().nullable(),
  color: z.string(),
  position: z.number(),
  instructions: z.string().nullable(),
  defaultAutonomyLevel: z.string().nullable(),
  // 0076 — canonical, machine-readable pipeline role. NULL on columns that
  // predate the migration and could not be classified by name. The UI uses
  // this to recognise e.g. the In Progress column without name-matching a
  // renameable display string.
  kind: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type OrgScopedColumn = z.infer<typeof orgScopedColumnSchema>;

export const createOrgScopedColumnSchema = z.object({
  slug: slugSchema.optional(),
  displayName: z.string().min(1).max(200),
  color: z.string().max(40).optional(),
  position: positionSchema.optional(),
  instructions: z.string().max(200_000).nullable().optional(),
  defaultAutonomyLevel: z.string().max(40).nullable().optional(),
});
export type CreateOrgScopedColumnInput = z.infer<typeof createOrgScopedColumnSchema>;

export const updateOrgScopedColumnSchema = z.object({
  slug: slugSchema.optional(),
  displayName: z.string().min(1).max(200).optional(),
  color: z.string().max(40).optional(),
  position: positionSchema.optional(),
  instructions: z.string().max(200_000).nullable().optional(),
  defaultAutonomyLevel: z.string().max(40).nullable().optional(),
});
export type UpdateOrgScopedColumnInput = z.infer<typeof updateOrgScopedColumnSchema>;

export const reorderOrgScopedColumnsSchema = z.object({
  items: z
    .array(
      z.object({
        id: z.string().uuid(),
        position: positionSchema,
      }),
    )
    .min(1),
});
export type ReorderOrgScopedColumnsInput = z.infer<typeof reorderOrgScopedColumnsSchema>;
